import type { MeResponse, NotificationDto } from '@process-hub/shared';
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api';
import { describeError } from '../errors';
import { formatWhen } from '../time';
import { OwnersCard } from './AdminOwners';
import { PeopleCard } from './AdminPeople';
import { describeEvent } from './Dashboard';

/**
 * Admin: people and roles (with their Teams connection, D-061), owners, and the notification
 * outbox — one row per person a card was, or will be, sent to.
 */
export function Admin({ me }: { me: MeResponse }) {
  const [notifications, setNotifications] = useState<NotificationDto[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(() => {
    if (!me.isAdmin) return;
    api
      .listNotifications(50)
      .then(setNotifications)
      .catch((e: unknown) => setError(describeError(e)));
  }, [me.isAdmin]);
  useEffect(load, [load]);

  if (!me.isAdmin) return <div className="notice">The Admin screen is for Process Hub administrators.</div>;
  if (error) return <div className="notice notice--error">{error}</div>;
  if (!notifications) return <div className="notice">Loading.</div>;

  const dispatchNow = async () => {
    try {
      const s = await api.dispatchNotifications();
      setNotice(`Dispatched: ${s.enqueued} new, ${s.sent} sent, ${s.failed} to retry, ${s.dead} given up, ${s.undelivered} not delivered.`);
      load();
    } catch (e) {
      setNotice(describeError(e, 'The dispatch could not be run.'));
    }
  };

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      {notice && (
        <div className="notice" role="status">
          {notice}
        </div>
      )}
      <PeopleCard me={me} onNotice={setNotice} />
      <OwnersCard onNotice={setNotice} />
      <div className="card">
        <div className="card__header">
          <div className="card__title">Notifications sent</div>
          <div className="card__meta">
            {notifications.length} most recent · one row per person · cards go out within seconds of an event and are retried for up to a day
            <button type="button" className="button button--secondary" style={{ marginLeft: 12 }} onClick={dispatchNow}>
              Dispatch now
            </button>
          </div>
        </div>
        {notifications.length === 0 ? (
          <div className="notice" style={{ margin: 18 }}>
            Nothing has been sent yet. Cards go to people connected to Teams on the People and roles card above, when something happens to a process that concerns them.
          </div>
        ) : (
          <table className="grid">
            <thead>
              <tr>
                <th>When</th>
                <th>Event</th>
                <th>To</th>
                <th>Process</th>
                <th>Department</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {notifications.map((n) => (
                <tr key={n.id}>
                  <td className="mono">{formatWhen(n.createdAt)}</td>
                  <td>{describeEvent(n.eventType)}</td>
                  <td>{n.recipientName ?? <span className="muted">nobody</span>}</td>
                  <td className="grid__primary">{n.slug ? <Link to={`/processes/${n.slug}`}>{n.processName}</Link> : (n.processName ?? '—')}</td>
                  <td>{n.departmentName}</td>
                  <td>
                    <span className={`pill pill--status-${STATUS_PILL[n.status]}`}>{STATUS_LABEL[n.status]}</span>
                    {n.status !== 'Sent' && n.lastError && (
                      <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
                        {n.lastError}
                        {n.status === 'Failed' && ` · retry ${formatWhen(n.nextAttemptAt)} (attempt ${n.attempts})`}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

const STATUS_LABEL: Record<NotificationDto['status'], string> = { Pending: 'Waiting', Sent: 'Sent', Failed: 'Will retry', Dead: 'Gave up', Undelivered: 'Not delivered' };
/** Reuse the process-status pill colours: blue done, gold in progress, orange needs attention, grey waiting or not sent. */
const STATUS_PILL: Record<NotificationDto['status'], string> = { Pending: 'Draft', Sent: 'Published', Failed: 'InApproval', Dead: 'Stale', Undelivered: 'Retired' };
